/**
 * WordPress dependencies
 */
import {
	Tooltip,
	UnitControl,
	parseQuantityAndUnitFromRawValue,
	type WPUnitControlUnit,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * Intrernal dependencies
 */
import './style.scss';

const CORNERS = {
	topLeft: _x( 'Top left', 'text', 'nelio-ab-testing' ),
	topRight: _x( 'Top right', 'text', 'nelio-ab-testing' ),
	bottomLeft: _x( 'Bottom left', 'text', 'nelio-ab-testing' ),
	bottomRight: _x( 'Bottom right', 'text', 'nelio-ab-testing' ),
};

type BoxInputControlsProps = {
	readonly className?: string;
	readonly values?:
		| {
				readonly flat?: string;
				readonly topLeft?: string;
				readonly topRight?: string;
				readonly bottomLeft?: string;
				readonly bottomRight?: string;
		  }
		| string;
	readonly readonlyCorners: {
		readonly topLeft: boolean;
		readonly topRight: boolean;
		readonly bottomLeft: boolean;
		readonly bottomRight: boolean;
	};
	readonly min?: number;
	readonly onChange: (
		value?:
			| string
			| {
					readonly flat?: string;
					readonly topLeft?: string;
					readonly topRight?: string;
					readonly bottomLeft?: string;
					readonly bottomRight?: string;
			  }
	) => void;
	readonly selectedUnits: {
		flat?: string;
		topLeft?: string;
		topRight?: string;
		bottomLeft?: string;
		bottomRight?: string;
	};
	readonly setSelectedUnits: ( units: {
		flat?: string;
		topLeft?: string;
		topRight?: string;
		bottomLeft?: string;
		bottomRight?: string;
	} ) => void;
	readonly units?: WPUnitControlUnit[];
};
export default function BoxInputControls( {
	onChange,
	className,
	selectedUnits,
	setSelectedUnits,
	values: valuesProp,
	readonlyCorners,
	...props
}: BoxInputControlsProps ): JSX.Element {
	const createHandleOnChange = ( corner: string ) => ( next?: string ) => {
		if ( ! onChange ) {
			return;
		}

		// Filter out CSS-unit-only values to prevent invalid styles.
		const isNumeric = !! next && ! isNaN( parseFloat( next ) );
		const nextValue = isNumeric ? next : undefined;

		onChange( {
			...values,
			[ corner ]: nextValue,
		} );
	};

	const createHandleOnUnitChange = ( side: string ) => ( next?: string ) => {
		const newUnits = { ...selectedUnits };
		newUnits[ side as keyof typeof newUnits ] = next;
		setSelectedUnits( newUnits );
	};

	// For shorthand style & backwards compatibility, handle flat string value.
	const values =
		typeof valuesProp !== 'string' && typeof valuesProp !== 'undefined'
			? valuesProp
			: {
					topLeft: valuesProp,
					topRight: valuesProp,
					bottomLeft: valuesProp,
					bottomRight: valuesProp,
			  };

	// Controls are wrapped in tooltips as visible labels aren't desired here.
	// Tooltip rendering also requires the UnitControl to be wrapped. See:
	// https://github.com/WordPress/gutenberg/pull/24966#issuecomment-685875026
	return (
		<div className="components-border-radius-control__input-controls-wrapper">
			{ Object.entries( CORNERS ).map( ( [ corner, label ] ) => {
				const [ parsedQuantity, parsedUnit ] =
					parseQuantityAndUnitFromRawValue(
						values[ corner as keyof typeof values ]
					);

				const computedUnit = values[ corner as keyof typeof values ]
					? parsedUnit
					: selectedUnits[ corner as keyof typeof values ] ||
					  selectedUnits.flat;

				return (
					<Tooltip text={ label } placement="top" key={ corner }>
						<div className="components-border-radius-control__tooltip-wrapper">
							<UnitControl
								{ ...props }
								className={ clsx( className, {
									'nab-components--disabled':
										corner in readonlyCorners &&
										readonlyCorners[
											corner as keyof typeof readonlyCorners
										],
								} ) }
								aria-label={ label }
								value={ [ parsedQuantity, computedUnit ].join(
									''
								) }
								onChange={ createHandleOnChange( corner ) }
								onUnitChange={ createHandleOnUnitChange(
									corner
								) }
								disabled={
									corner in readonlyCorners &&
									readonlyCorners[
										corner as keyof typeof readonlyCorners
									]
								}
								size="__unstable-large"
							/>
						</div>
					</Tooltip>
				);
			} ) }
		</div>
	);
}
